import React, { useState } from 'react'
import { Button, Modal } from 'react-bootstrap'
import { useAtomValue } from 'jotai'

import * as state from '../../states'
import { IFetchItemsSetup } from '../../types'
import { OfferListTable } from './OfferListTable'
import { useOfferList, useFiltersOfferListModal, useSortOfferListModal } from '../../hooks'
import { ToolPanel, UiPagination, ESortOrder } from '../ui'
import { Filters, FiltersButtonIcon } from '../filters'
import { EFilterOperator } from '../../types/enum'
import { Access } from '../../lib'
import { useTranslation } from 'react-i18next'
import { ALL_LANG_KEY } from '../../types/lang'

type IOfferUnscopedListModalProps = {
	group: string;
	show: boolean;
	take: number | 'all';
	onClose: () => void;
}

export const OfferUnscopedListModal = (
	props: IOfferUnscopedListModalProps
) => {
	const { t } = useTranslation()
	const { group, show, take = 10, onClose } = props

	const profile = useAtomValue(state.profileAtom)
	const $access = profile ? new Access(profile) : null

	const [page, setPage] = useState<number>(0)
	const skip = (take === 'all') ? 0 : page > 0 ? page * take : 0

	const {
		filter,
		send,
		activeFiltersCount,
		filterFillFactor,
		isFiltersPanelOpen,
		setIsFiltersPanelOpen,
		offerModalFiltersConfig,
		defaultFiltersValue,
		initialFiltersValue,
		clearChildFilters,
		setUserFilters
	} = useFiltersOfferListModal(setPage)

	const {
		sortBy,
		setSortBy,
		order,
		setOrder
	} = useSortOfferListModal()

	const setup: IFetchItemsSetup = {
		filter: [[`scopeId${EFilterOperator.isNull}`, null], ...(group ? [['group+=', group]] as [string, any][] : []), ...filter],
		sort: order !== ESortOrder.unset ? [[sortBy, order]] : [],
		skip,
		take
	}
	const { error, items, total, warmUp } = useOfferList(setup, send)

	const onBind = undefined
	const onUnBind = undefined // (item: IOffer, e: React.MouseEvent) => { }

	const modalTitle = group ? `${t(ALL_LANG_KEY.offer_list_modal.title_unscoped_in_group)} #${group}` : t(ALL_LANG_KEY.offer_list_modal.title_unscoped)

	return <>
		<Modal show={show} onHide={onClose} size="xl" className={'modal-with-sticky scroll'}>
			<Modal.Header className={'modal-header--sticky'} closeButton>
				<FiltersButtonIcon
					activeFiltersCount={activeFiltersCount}
					filterFillFactor={filterFillFactor}
					onClick={() => setIsFiltersPanelOpen((val) => !val)}
					styles={{ marginRight: 'auto' }}
				/>

				<Modal.Title>{modalTitle}</Modal.Title>
			</Modal.Header>

			<Modal.Body>
				<Filters
					classesToAdd={{ filters: 'filters-offer-modal' }}
					config={offerModalFiltersConfig}
					initialFiltersValue={defaultFiltersValue}
					resetFiltersValue={initialFiltersValue}
					isAutoModeWithDebounce={false}
					isFiltersPanelOpen={isFiltersPanelOpen}
					isFiltersToClear={clearChildFilters}
					setFiltersCB={(value) => {
						setUserFilters(value)
					}}
				/>

				<OfferListTable
					items={items}
					skip={skip}
					sortBy={sortBy}
					setSortBy={setSortBy}
					order={order}
					setOrder={setOrder}
					onBind={onBind}
					onUnBind={onUnBind}
				/>
			</Modal.Body>

			<Modal.Footer className={'modal-footer--sticky'}>
				<ToolPanel flow=">-|-<" width={'100%'}>
					<Button variant="secondary" onClick={onClose}>{`${t(ALL_LANG_KEY.offer_list_modal.btn_close)}`}</Button>

					<UiPagination
						page={page}
						take={take}
						total={total}
						click={(page: number, e: React.MouseEvent) => {
							e.preventDefault()
							setPage(page)
						}}
					/>
				</ToolPanel>

			</Modal.Footer>
		</Modal>
	</>
}
